/**
 * Landing page component with mode selection and age picker
 * Refactored for best React, TypeScript, and DRY practices
 */

import { useEffect } from "react";
import ErrorBoundary from "./ErrorBoundary";
import { createTheme } from "../styles/theme";
import { cn } from "../lib/utils";
import { focusStyles, hoverStyles } from "../utils/styles";
import { useLandingPageState } from "../hooks/useLandingPage";
import { MODE_OPTIONS, APP_CONFIG } from "../constants/landing";
import { HeroSection } from "./preLaunch/PreLaunchContent";
import { ModeCard } from "./landing/ModeCard";
import { AgeSelector } from "./landing/AgeSelector";
import { LegalNotice } from "./landing/LegalNotice";
import { ContinueButton } from "./landing/ContinueButton";

function LandingPage() {
  const theme = createTheme(false); // Default to dark mode for landing
  const { state, actions, canContinue, getContinuePath, resetState } = useLandingPageState();

  // Reset state when landing page mounts to show mode buttons
  useEffect(() => {
    resetState();
  }, [resetState]);

  return (
    <ErrorBoundary>
      <main
        className="min-h-screen landing-page-bg"
        role="main"
        aria-label="Find a Feeling - Mode Selection"
      >
        {/* Fixed Hero Section at top */}
        <div className="xs:pt-4 xs:pb-4 pt-8 pb-6 px-4 sm:pt-20 md:pt-28 md:pb-12 lg:pt-30 lg:pb-10 xl:pt-42 xl:pb-14 sm:px-8">
          <HeroSection
            title={APP_CONFIG.title}
            subtitle={APP_CONFIG.subtitle}
            titleClassName={theme.colors.text.title}
            subtitleClassName={theme.colors.text.primary}
            containerClassName="pt-0 sm:pt-8"
          />
        </div>

        {/* Content Area - Centered below hero */}
        <div className="flex flex-col items-center justify-center px-4 pb-2 sm:px-8 sm:pb-8">
          {/* Mode Selection Cards - Only show if no mode selected */}
          {!state.selectedMode && (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 sm:gap-6 max-w-sm sm:max-w-4xl mx-auto w-full">
              {MODE_OPTIONS.map(option => (
                <ModeCard
                  key={option.value}
                  option={option}
                  isSelected={state.selectedMode === option.value}
                  onClick={() => actions.handleModeSelect(option.value)}
                  theme={theme}
                />
              ))}
            </div>
          )}

          {/* Age Selection - Only for Explorer Mode */}
          {state.selectedMode === "explorer" && (
            <div className="mb-4">
              <AgeSelector
                selectedAge={state.selectedAge}
                onAgeSelect={actions.setSelectedAge}
                theme={theme}
              />
            </div>
          )}

          {/* Legal Notice - Only for Explorer (Kids) mode */}
          {state.selectedMode === "explorer" && (
            <div className="mb-4">
              <LegalNotice
                hasConsented={state.hasConsented}
                onConsentChange={actions.setHasConsented}
                theme={theme}
              />
            </div>
          )}

          {/* Action Buttons - Back and Continue */}
          {state.selectedMode === "explorer" && (
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 items-center justify-center w-full">
              <button
                onClick={() => actions.setSelectedMode(null)}
                aria-label="Go back to mode selection"
                className={cn(
                  "min-h-[2rem] sm:min-h-[2.5rem] px-4 sm:px-8 py-1 sm:py-2 text-base sm:text-xl font-bold rounded-full",
                  "border-2 border-border-default bg-surface-alt text-text-primary hover:border-brand-secondary",
                  "inline-flex items-center justify-center",
                  focusStyles.ring,
                  hoverStyles.scale
                )}
              >
                ← Back
              </button>
              <ContinueButton
                canContinue={canContinue}
                continuePath={getContinuePath()}
                theme={theme}
              />
            </div>
          )}
        </div>
      </main>
    </ErrorBoundary>
  );
}

export default LandingPage;
